import type { WebMcpModelContext } from './webmcp';
import type { WebMcpToolName } from './types';

/** Empirical ChatGPT browser ceiling: 102 discovered tools disables WebMCP; keep the registry at or below 100. */
export const MAX_REGISTERED_TOOLS = 100;

export type ToolSchema = ToolSchemaObject | ToolSchemaArray | ToolSchemaInteger | ToolSchemaNumber | ToolSchemaBoolean | ToolSchemaString | ToolSchemaEnum | ToolSchemaOneOf | ToolSchemaAnyOf | ToolSchemaReference;
export type ToolSchemaEnumValue = boolean | number | string;

interface ToolSchemaBase { readonly description?: string; readonly enum?: readonly ToolSchemaEnumValue[]; readonly $defs?: Readonly<Record<string, ToolSchema>>; }
export interface ToolSchemaObject extends ToolSchemaBase { readonly type: 'object'; readonly properties?: Readonly<Record<string, ToolSchema>>; readonly required?: readonly string[]; readonly additionalProperties?: boolean; }
export interface ToolSchemaArray extends ToolSchemaBase { readonly type: 'array'; readonly items: ToolSchema; readonly minItems?: number; readonly maxItems?: number; }
export interface ToolSchemaInteger extends ToolSchemaBase { readonly type: 'integer'; readonly minimum?: number; readonly maximum?: number; }
export interface ToolSchemaNumber extends ToolSchemaBase { readonly type: 'number'; readonly minimum?: number; readonly maximum?: number; }
export interface ToolSchemaBoolean extends ToolSchemaBase { readonly type: 'boolean'; }
export interface ToolSchemaString extends ToolSchemaBase { readonly type: 'string'; readonly minLength?: number; readonly maxLength?: number; readonly pattern?: string; }
export interface ToolSchemaEnum extends ToolSchemaBase { readonly enum: readonly [ToolSchemaEnumValue, ...ToolSchemaEnumValue[]]; }
export interface ToolSchemaOneOf extends ToolSchemaBase { readonly oneOf: readonly [ToolSchema, ...ToolSchema[]]; }
export interface ToolSchemaAnyOf extends ToolSchemaBase { readonly anyOf: readonly [ToolSchema, ...ToolSchema[]]; }
/** A local JSON Schema reference, typically resolved through a root `$defs` member. */
export interface ToolSchemaReference extends ToolSchemaBase { readonly $ref: string; }
export type WebMcpInputSchema = ToolSchema;

export type WebMcpValue = boolean | number | string | null | readonly WebMcpValue[] | { readonly [name: string]: WebMcpValue };
export interface WebMcpRequest { readonly [name: string]: WebMcpValue | undefined; }
export interface ToolTextContent { readonly type: 'text'; readonly text: string; }
export interface ToolResponse { readonly content: readonly ToolTextContent[]; }
export interface ToolAnnotations { readonly readOnlyHint?: boolean; readonly destructiveHint?: boolean; readonly idempotentHint?: boolean; readonly openWorldHint?: boolean; readonly untrustedContentHint?: boolean; }

export interface WebMcpCapabilities {
    readonly structureCount: number; readonly hasStructure: boolean; readonly hasVolume: boolean; readonly hasTrajectory: boolean; readonly hasCoordinates: boolean; readonly hasTunnelState: boolean; readonly hasPdbEntry: boolean; readonly hasAssemblies: boolean; readonly isolateActive: boolean; readonly hasPendingProposals: boolean;
}

export interface ToolDefinition { readonly name: WebMcpToolName; readonly description: string; readonly inputSchema: WebMcpInputSchema; readonly execute: (input: WebMcpRequest, signal: AbortSignal) => ToolResponse | Promise<ToolResponse>; readonly annotations?: ToolAnnotations; readonly isAvailable?: (capabilities: WebMcpCapabilities) => boolean; }
export function isWebMcpRequest(value: unknown): value is WebMcpRequest { return value !== null && typeof value === 'object' && !Array.isArray(value); }

function hasModelContext(value: Document): value is Document & { readonly modelContext: WebMcpModelContext } { return value.modelContext !== void 0; }

/** Registers applicable tools. Aborting the registration signal unregisters a tool. */
export class WebMcpToolRegistration {
    private readonly controllers = new Map<WebMcpToolName, AbortController>();
    private readonly activeExecutions = new Map<WebMcpToolName, number>();
    private readonly idleWaiters: (() => void)[] = [];
    private pending = Promise.resolve();
    private capabilities: WebMcpCapabilities | undefined;
    private generation = 0;
    private disposed = false;

    static create(document: Document, definitions: readonly ToolDefinition[]) { return hasModelContext(document) ? new WebMcpToolRegistration(document.modelContext, definitions) : void 0; }
    update(capabilities: WebMcpCapabilities) {
        if (this.capabilities !== void 0 && sameCapabilities(this.capabilities, capabilities)) return this.pending;
        this.capabilities = capabilities;
        const generation = ++this.generation;
        this.pending = this.pending.catch(() => {}).then(() => this.sync(capabilities, generation));
        return this.pending;
    }
    dispose() { this.disposed = true; ++this.generation; for (const controller of this.controllers.values()) controller.abort(); this.controllers.clear(); this.resumeIdleWaiters(); }

    private async sync(capabilities: WebMcpCapabilities, generation: number) {
        await this.waitForIdle();
        if (this.disposed || generation !== this.generation) return;
        const definitions = new Map<WebMcpToolName, ToolDefinition>();
        for (const definition of this.definitions) {
            if (definitions.has(definition.name)) continue;
            if (!definition.isAvailable || definition.isAvailable(capabilities)) definitions.set(definition.name, definition);
        }
        if (definitions.size > MAX_REGISTERED_TOOLS) {
            for (const controller of this.controllers.values()) controller.abort();
            this.controllers.clear();
            throw new Error(`WebMCP registry has ${definitions.size} tools; the client limit is ${MAX_REGISTERED_TOOLS}.`);
        }
        for (const [name, controller] of this.controllers) if (!definitions.has(name)) { controller.abort(); this.controllers.delete(name); }
        for (const [name, definition] of definitions) {
            if (this.controllers.has(name)) continue;
            await this.waitForIdle();
            if (this.disposed || generation !== this.generation) return;
            const controller = new AbortController();
            await this.modelContext.registerTool({ name: definition.name, title: definition.name, description: definition.description, inputSchema: definition.inputSchema, annotations: definition.annotations, execute: (input, options) => this.execute(definition, input, options?.signal ?? controller.signal) }, { signal: controller.signal });
            if (this.disposed || generation !== this.generation) { controller.abort(); return; }
            this.controllers.set(name, controller);
        }
    }

    private async execute(definition: ToolDefinition, input: unknown, signal: AbortSignal) {
        this.activeExecutions.set(definition.name, (this.activeExecutions.get(definition.name) ?? 0) + 1);
        try {
            return await executeTool(definition, input, signal);
        } finally {
            const remaining = (this.activeExecutions.get(definition.name) ?? 1) - 1;
            if (remaining === 0) {
                this.activeExecutions.delete(definition.name);
                if (this.activeExecutions.size === 0) setTimeout(() => this.resumeIdleWaiters(), 0);
            } else {
                this.activeExecutions.set(definition.name, remaining);
            }
        }
    }

    private waitForIdle() {
        if (this.activeExecutions.size === 0) return Promise.resolve();
        return new Promise<void>(resolve => this.idleWaiters.push(resolve));
    }

    private resumeIdleWaiters() {
        if (this.activeExecutions.size > 0) return;
        for (const resolve of this.idleWaiters.splice(0)) resolve();
    }

    private constructor(private readonly modelContext: WebMcpModelContext, private readonly definitions: readonly ToolDefinition[]) {}
}

async function executeTool(definition: ToolDefinition, input: unknown, signal: AbortSignal): Promise<ToolResponse> {
    if (!isWebMcpRequest(input)) return responseFailure('Invalid tool input.', 'Provide an object matching the tool input schema.');
    if (signal.aborted) return responseFailure('Tool request cancelled.', 'Retry the request when the client is ready to receive the result.');
    try {
        const response = await definition.execute(input, signal);
        return signal.aborted ? responseFailure('Tool request cancelled.', 'Retry the request when the client is ready to receive the result.') : response;
    } catch (error) {
        return responseFailure('Tool execution failed.', error instanceof Error ? error.message : 'Retry with valid loaded viewer data.');
    }
}

function responseFailure(error: string, hint: string): ToolResponse {
    return { content: [{ type: 'text', text: JSON.stringify({ ok: false, error, hint }) }] };
}

function sameCapabilities(a: WebMcpCapabilities, b: WebMcpCapabilities) {
    return a.structureCount === b.structureCount
        && a.hasStructure === b.hasStructure
        && a.hasVolume === b.hasVolume
        && a.hasTrajectory === b.hasTrajectory
        && a.hasCoordinates === b.hasCoordinates
        && a.hasTunnelState === b.hasTunnelState
        && a.hasPdbEntry === b.hasPdbEntry
        && a.hasAssemblies === b.hasAssemblies
        && a.isolateActive === b.isolateActive
        && a.hasPendingProposals === b.hasPendingProposals;
}
